import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { throttle } from "../../utils/throttle";

@Component({
    tag: "ux-table",
    styleUrl: "table.scss",
    shadow: false
})
export class Table {
    /** flag which determines if fixed column mechanism should be used */
    // eslint-disable-next-line @stencil-community/ban-default-true
    @Prop() readonly useFixedColumn: boolean = true;
    /** zero-based index of the column that should be fixed on smaller viewports - defaults to the first col */
    @Prop() readonly fixedColIndex: number = 0;
    /** zero-based index of the column that should be used for the title column for the mobile version
     * - defaults to the first col */
    @Prop() readonly mobileTitleIndex: number = 0;
    /** maximum number of pixels that the fixed column is allowed to be wide */
    @Prop() readonly maxFixedColWidth: number = 250;
    /** the table should always be at least 100% wide */
    @Prop() readonly fullWidth: boolean = false;
    /** the table should sort its static data by itself. Do not use this if the data needs to be sorted by some
     * logic (i.e. via an API) */
    @Prop() readonly sortable: boolean = false;
    /** Label for the ux-select that is shown for the sorting of mobile tables */
    @Prop() readonly mobileSortLabel: string = "";
    /** The link in the last column will be added into the fixed column on smaller viewports */
    @Prop() readonly hasFixedLink: boolean = false;
    /** setting this to false disables the mobile accordion - showing the scrollable table instead also on smaller screens */
    // eslint-disable-next-line @stencil-community/ban-default-true
    @Prop() readonly mobileAccordion: boolean = true;
    /** no-click-selector for ux-accordion in mobile table – use if you use an icon in the mobile-fixed-col */
    @Prop() readonly noClickSelector: string = "";
    /** makes every column the same width, no matter the content */
    @Prop() readonly equalColumnWidth: boolean = false;
    /** makes background colors of the table rows change */
    @Prop() readonly striped: boolean = false;
    /** is true when the table is horizontally scrollable (has a fixed col) */
    @State() isFixed = false;
    /** width of the fixed col */
    @State() fixedColWidth: number = this.useFixedColumn ? 250 : 0;
    /** the left shadow is visible */
    @State() leftShadow = false;
    /** the right shadow is visible */
    @State() rightShadow = false;
    /** unique ID used to style this instance via style tag */
    @State() uniqueId!: string;

    /** checks for IE11 for IntersectionObserver support */
    private supportsIntersectionObserver = "IntersectionObserver" in window;

    /** if hasFixedLink is active, we need to add more width to the fixed cell,
     * but only once. Once this flag is set to true, this addition will not be
     * performed, avoiding useless space being added in the table cells.
     */
    @State() fixedLinkSpaceAdded = false;
    /** the fixed column should have a min width set to fit-content to prevent being
     * calculated too small, but if the content of the column exceeds the maximun width
     * of the column specified via prop, then the min-width should be set to auto.
     */
    @State() fixedColumnHasMinWidth = true;

    @Element() element!: HTMLUxTableElement;

    private contentObserver: MutationObserver = new MutationObserver(debounce(() => this.onContentChange(), 100));

    private handleIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.map((entry) => {
            if (entry.isIntersecting) {
                this.onContentChange();
            }
        });
    };
    private intersectionObserver = this.supportsIntersectionObserver ? new IntersectionObserver(this.handleIntersection) : null;

    private table: HTMLTableElement;
    private innerDiv: HTMLDivElement;
    private mobileTable: HTMLUxMobileTableElement;

    connectedCallback() {
        this.uniqueId = `ux-table-id-${idCounter++}`;
        this.table = this.element.querySelector("table") as HTMLTableElement;
        window.addEventListener("resize", this.onResize);
    }

    componentDidLoad() {
        if (this.useFixedColumn) {
            this.addFixedLink();
            this.calculateFixedColWidth();
            this.calculateTableState();
            this.syncRowHeight();
        }

        this.calculateShadows();

        this.contentObserver.observe(this.innerDiv, {
            childList: true,
            subtree: true
        });
        if (!this.supportsIntersectionObserver) {
            return;
        }
        // @ts-expect-error Check for typing
        this.intersectionObserver.observe(this.innerDiv);
    }

    disconnectedCallback() {
        window.removeEventListener("resize", this.onResize);
        this.contentObserver.disconnect();
        if (!this.supportsIntersectionObserver) {
            return;
        }
        // @ts-expect-error Check for typing
        this.intersectionObserver.disconnect();
    }

    private onContentChange() {
        if (this.useFixedColumn) {
            this.addFixedLink();
            this.syncRowHeight();
            this.calculateFixedColWidth();
            this.calculateTableState();
        }
        if (this.mobileAccordion) {
            this.mobileTable.update();
        }
        this.calculateShadows();
    }

    private onResize = throttle(() => {
        this.calculateFixedColWidth();
        this.calculateTableState();
    }, 200);

    private calculateTableState() {
        if (this.useFixedColumn) {
            const containerWidth = this.element.getBoundingClientRect().width;
            const tableRect = this.table.getBoundingClientRect();
            const tableWidth = tableRect.width + (this.isFixed ? this.fixedColWidth : 0);
            this.isFixed = containerWidth < tableWidth;
        }
    }

    private calculateFixedColWidth() {
        const { table, fixedColIndex, maxFixedColWidth } = this;
        if (table.clientHeight === 0) {
            return;
        }
        const fixedCells = table.querySelectorAll(
            `td:nth-child(${fixedColIndex + 1})
            ,th:nth-child(${fixedColIndex + 1})`
        );
        const fixedCellsArray = [].slice.call(fixedCells);
        let width = 0;
        // we only calculate the width for the first 50 items to prevent that bigger tables get slow.
        fixedCellsArray.slice(0, 50).forEach((cell: HTMLTableDataCellElement) => {
            const cellWidth = cell.getBoundingClientRect().width;
            width = cellWidth > width ? cellWidth : width;
        });
        if (!this.fixedLinkSpaceAdded && width !== 0 && this.hasFixedLink) {
            width = width + 15;
            this.fixedLinkSpaceAdded = true;
        }
        this.fixedColWidth = width > maxFixedColWidth || width === 0 ? maxFixedColWidth : width;
        this.fixedColumnHasMinWidth = this.fixedColWidth !== maxFixedColWidth;
    }

    private onScroll = throttle(() => this.calculateShadows(), 10);

    @Watch("isFixed")
    private calculateShadows() {
        const scrollLeft = this.innerDiv.scrollLeft;
        const containerWidth = this.innerDiv.getBoundingClientRect().width;
        const tableWidth = this.table.getBoundingClientRect().width + (this.isFixed ? this.fixedColWidth : 0);
        /** small puffer for the calculation of the right shadow, fixes the
         * bug that the shadow was not removed for 0.1px of discrepancy
         */
        const rightShadowPuffer = 2;
        this.leftShadow = (this.isFixed || !this.useFixedColumn) && scrollLeft > 0;
        this.rightShadow = scrollLeft + containerWidth + rightShadowPuffer < tableWidth;
    }

    /** the height of multi-line cells might differ from the fixed cell, so we need to sync it to prevent gaps */
    @Watch("isFixed")
    private syncRowHeight() {
        const rows = this.table.querySelectorAll("tr");
        [].forEach.call(rows, (row: HTMLTableRowElement) => {
            const rowHeight = row.getBoundingClientRect().height;
            // @ts-expect-error Check for typing
            const fixedCell: HTMLTableDataCellElement | HTMLTableHeaderCellElement = row.querySelector(
                `th:nth-child(${this.fixedColIndex + 1}),td:nth-child(${this.fixedColIndex + 1})`
            );
            const fixedCellHeight = fixedCell.getBoundingClientRect().height;
            const height = Math.max(rowHeight, fixedCellHeight);
            row.style.height = `${height}px`;
            fixedCell.style.height = `${height}px`;
        });
    }

    @Listen("uxTableSort")
    sortRows(e: CustomEvent) {
        if (!this.sortable) {
            return;
        }
        const numerical = e.detail.numerical;
        const sorter = e.target as HTMLUxTableSortElement;
        const th = sorter.closest("th");
        // @ts-expect-error Check for typing
        const titleRow = th.parentElement;
        // @ts-expect-error Check for typing
        const colIndex = [].indexOf.call(titleRow.children, th);
        // @ts-expect-error Check for typing
        const table = th.closest("table");
        const sortAttr = "data-sort";

        const getCellValue = (tr: HTMLTableRowElement, idx: number) =>
            tr.children[idx].hasAttribute(sortAttr) ? tr.children[idx].getAttribute(sortAttr) : tr.children[idx].textContent;

        const comparer = (idx: number, asc: boolean) => (a: HTMLTableRowElement, b: HTMLTableRowElement) => {
            return numerical
                ? // @ts-expect-error Check for typing
                  getCellValue(asc ? a : b, idx).localeCompare(getCellValue(asc ? b : a, idx), undefined, { numeric: true })
                : // @ts-expect-error Check for typing
                  getCellValue(asc ? a : b, idx).localeCompare(getCellValue(asc ? b : a, idx));
        };

        [].slice
            // @ts-expect-error Check for typing
            .call(table.querySelectorAll(`tr`))
            .filter((tr: HTMLTableRowElement) => !tr.isSameNode(titleRow))
            .sort(comparer(colIndex, sorter.sortedDirection === "asc"))
            // @ts-expect-error Check for typing
            .forEach((tr: HTMLTableRowElement) => table.appendChild(tr));
    }

    private addFixedLink(): void {
        if (!this.hasFixedLink) {
            return;
        }

        const { table } = this;
        const tableRows = table.querySelectorAll(`tr`);
        const tableRowsArray = [].slice.call(tableRows);
        const fixedLinkClass = "ux-table__fixed-link";
        tableRowsArray.forEach((tableRow: HTMLElement): void => {
            const lastCellIndex = tableRow.children.length - 1;
            const lastRowCell = tableRow.children[lastCellIndex];
            const link = lastRowCell.querySelector("a");
            if (!link) {
                return;
            }
            const firstRowCell = tableRow.children[this.fixedColIndex];
            const hasFixedLink = firstRowCell.querySelector(`.${fixedLinkClass}`);
            if (!hasFixedLink) {
                const clonedLink = link.cloneNode(true) as HTMLElement;
                clonedLink.className = fixedLinkClass;
                clonedLink.innerHTML = '<ux-icon name="chevron-right" weight="normal"></ux-icon>';
                firstRowCell.insertAdjacentElement("beforeend", clonedLink);
            }
        });
    }

    render() {
        const baseClass = "ux-table";
        const fixedClass = `${baseClass}--is-fixed`;
        const {
            uniqueId,
            isFixed,
            fixedColIndex,
            fixedColWidth,
            maxFixedColWidth,
            leftShadow,
            rightShadow,
            fullWidth,
            fixedColumnHasMinWidth,
            useFixedColumn,
            mobileAccordion,
            equalColumnWidth
        } = this;
        const fixedCol = fixedColIndex + 1; // CSS selectors work with an 1-based index
        const { table } = this;
        const lastRow = table.rows[table.rows.length - 1];
        const lastCellIndex = lastRow.cells[lastRow.cells.length - 1].cellIndex;
        const columnCount = table.rows[0].cells.length;

        return (
            <Host
                class={{
                    [baseClass]: true
                }}
            >
                <style>{`
                    #${uniqueId} th:nth-child(${fixedCol}),
                    #${uniqueId} td:nth-child(${fixedCol}) {
                        max-width: ${maxFixedColWidth}px;
                    }

                    .${fixedClass} #${uniqueId} th:nth-child(${fixedCol}),
                    .${fixedClass} #${uniqueId} td:nth-child(${fixedCol}) {
                        position: absolute;
                        left: 0;
                        width: ${fixedColWidth}px;
                        min-width: ${fixedColumnHasMinWidth ? `fit-content` : `auto`};
                        display:flex;
                        align-items: center;
                        justify-content: flex-start;
                        border-left: 1px solid transparent;
                    }

                    .${fixedClass} #${uniqueId} th:nth-child(${fixedCol}) {
                        border-right: 1px solid #9ea9b0;
                    }

                    .${fixedClass} #${uniqueId} td:nth-child(${fixedCol}) {
                        border-top: 1px solid transparent;
                        border-right: 1px solid #cbcbcb;
                    }

                    .${fixedClass} #${uniqueId} th:nth-child(${lastCellIndex === fixedColIndex ? lastCellIndex : 0}),
                    .${fixedClass} #${uniqueId} td:nth-child(${lastCellIndex === fixedColIndex ? lastCellIndex : 0}) {
                        border-right: 1px solid transparent !important;
                    }

                    .${fixedClass} #${uniqueId} table {
                        margin-left: ${fixedColWidth}px;
                    }

                    #${uniqueId}:before {
                        left: ${fixedColWidth}px;
                    }

                    ${
                        equalColumnWidth
                            ? `td {
                            width: ${100 / columnCount}%;
                        }`
                            : ""
                    }

                `}</style>
                <div
                    class={{
                        [`${baseClass}--hide-on-mobile`]: mobileAccordion,
                        [fixedClass]: isFixed,
                        [`${baseClass}--has-left-shadow`]: leftShadow,
                        [`${baseClass}--non-fixed-column`]: !useFixedColumn,
                        [`${baseClass}--has-right-shadow`]: rightShadow,
                        [`${baseClass}--full-width`]: fullWidth,
                        [`${baseClass}--same-column-width`]: equalColumnWidth
                    }}
                >
                    <div
                        class={`${baseClass}__inner`}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.innerDiv = el)}
                        onScroll={() => this.onScroll()}
                        id={uniqueId}
                        data-scroll-target
                    >
                        <slot />
                    </div>
                </div>
                {mobileAccordion && (
                    <ux-mobile-table
                        sortable={this.sortable}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.mobileTable = el)}
                        uxTable={this.element}
                        selectLabel={this.mobileSortLabel}
                        noClickSelector={this.noClickSelector}
                    />
                )}
            </Host>
        );
    }
}

/** this variable is incremented by each instance */
let idCounter = 0;
